/**
 * LoginPage Component
 *
 * The main login page for Auth.HireNow.com.
 * Provides a provider-agnostic sign-in interface that works with
 * any configured authentication adapter.
 */

import { useState, useCallback } from 'react';
import { useAuth } from '../auth';
import { Loading } from './Loading';

/**
 * LoginPage Component
 *
 * Displays the HireNow branding and a sign-in button.
 * Handles authentication errors and loading states.
 */
export function LoginPage() {
  const { login, isLoading, error, clearError } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);

  /**
   * Handle sign-in button click
   * Triggers the authentication flow via the active provider
   */
  const handleSignIn = useCallback(async () => {
    setIsSigningIn(true);
    clearError();

    try {
      // Use popup flow for better UX (doesn't navigate away)
      await login({ interaction: 'popup' });
    } catch (err) {
      // Error is already captured in context, but log for debugging
      console.error('[LoginPage] Sign-in failed:', err);
    } finally {
      setIsSigningIn(false);
    }
  }, [login, clearError]);

  /**
   * Handle sign-in with redirect (alternative flow)
   * Useful for browsers that block popups
   */
  const handleSignInRedirect = useCallback(async () => {
    setIsSigningIn(true);
    clearError();

    try {
      await login({ interaction: 'redirect' });
      // Note: This will navigate away, so we won't reach the finally block
    } catch (err) {
      console.error('[LoginPage] Sign-in redirect failed:', err);
      setIsSigningIn(false);
    }
  }, [login, clearError]);

  // Show loading state during initial auth check
  if (isLoading && !isSigningIn) {
    return <Loading fullScreen message="Checking authentication..." />;
  }

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Logo and Branding */}
        <div className="login-header">
          <div className="logo">
            <svg
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="logo-icon"
              aria-hidden="true"
            >
              <rect width="40" height="40" rx="8" fill="#0066CC" />
              <path
                d="M12 14h6v12h-6zM22 14h6v12h-6zM12 20h16"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <span className="logo-text">HireNow</span>
          </div>
          <h1 className="login-title">Welcome</h1>
          <p className="login-subtitle">
            Sign in to access your HireNow Administrator account
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="login-error" role="alert">
            <span className="error-icon" aria-hidden="true">
              !
            </span>
            <div className="error-content">
              <p className="error-message">{error.message}</p>
              <button
                type="button"
                className="error-dismiss"
                onClick={clearError}
                aria-label="Dismiss error"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Sign-in Actions */}
        <div className="login-actions">
          <button
            type="button"
            className="btn btn-primary btn-signin"
            onClick={handleSignIn}
            disabled={isSigningIn}
            aria-busy={isSigningIn}
          >
            {isSigningIn ? (
              <>
                <span className="btn-spinner" aria-hidden="true" />
                Signing in...
              </>
            ) : (
              <>
                <MicrosoftIcon />
                Sign in with Microsoft
              </>
            )}
          </button>

          {/* Alternative: Redirect-based sign-in */}
          <button
            type="button"
            className="btn btn-secondary btn-signin-alt"
            onClick={handleSignInRedirect}
            disabled={isSigningIn}
          >
            Having trouble? Use redirect sign-in
          </button>
        </div>

        {/* Footer */}
        <div className="login-footer">
          <p className="login-help">
            Need help?{' '}
            <a href="mailto:support@hirenow.com" className="help-link">
              Contact Support
            </a>
          </p>
          <p className="login-terms">
            By signing in, you agree to our{' '}
            <a href="/terms" className="terms-link">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" className="privacy-link">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Microsoft Icon Component
 * SVG icon for the Microsoft sign-in button
 */
function MicrosoftIcon() {
  return (
    <svg
      className="btn-icon"
      viewBox="0 0 21 21"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}
